import type {
  Device,
  DeviceHistoryEntry,
  DeviceUpdate,
  LabelKind,
  ListResponse,
  LpnLease,
  PrintJob,
  PrintJobResult,
  Printer,
  PrinterCreate,
  PrinterStatus,
  PrinterUpdate,
  TemplateInfo,
} from '@warehouse/shared';

import { api, apiRequest } from '../../lib/api-client';
import { listQueryParams, type ListState } from '../../lib/list-query';

/**
 * The devices-printing HTTP surface, in one file so a page never hand-rolls a
 * fetch. Note what no call here accepts: a printer HOST, a PORT, or ZPL. The
 * client names a registered printer id and a typed payload; the template and
 * the socket target are the server's business (the old system let the browser
 * pass both — docs/recon/printing-scanners.md A6 #8).
 */

// --- devices ---------------------------------------------------------------

export function listDevices(state: ListState): Promise<ListResponse<Device>> {
  return api.get<ListResponse<Device>>('/devices', { query: listQueryParams(state) });
}

export function getDevice(id: string): Promise<Device> {
  return api.get<Device>(`/devices/${id}`);
}

export function getDeviceHistory(id: string): Promise<{ items: DeviceHistoryEntry[] }> {
  return api.get<{ items: DeviceHistoryEntry[] }>(`/devices/${id}/history`);
}

export function updateDevice(id: string, patch: DeviceUpdate): Promise<Device> {
  return api.patch<Device>(`/devices/${id}`, patch);
}

export function leaseLpnRange(id: string, force = true): Promise<LpnLease> {
  return api.post<LpnLease>(`/devices/${id}/lpn-lease`, { force });
}

// --- printers --------------------------------------------------------------

export function listPrinters(state: ListState): Promise<ListResponse<Printer>> {
  return api.get<ListResponse<Printer>>('/printers', { query: listQueryParams(state) });
}

export function createPrinter(input: PrinterCreate): Promise<Printer> {
  return api.post<Printer>('/printers', input);
}

export function updatePrinter(id: string, patch: PrinterUpdate): Promise<Printer> {
  return api.patch<Printer>(`/printers/${id}`, patch);
}

export function deletePrinter(id: string): Promise<{ id: string }> {
  return api.delete<{ id: string }>(`/printers/${id}`);
}

export function testPrint(id: string, note?: string): Promise<PrintJobResult> {
  return api.post<PrintJobResult>(`/printers/${id}/test-print`, note ? { note } : {});
}

export function pollPrinterStatus(id: string): Promise<PrinterStatus> {
  return api.post<PrinterStatus>(`/printers/${id}/status`, {});
}

// --- print jobs ------------------------------------------------------------

export function listPrintJobs(state: ListState): Promise<ListResponse<PrintJob>> {
  return api.get<ListResponse<PrintJob>>('/print/jobs', { query: listQueryParams(state) });
}

export function getPrintJob(id: string): Promise<PrintJob> {
  return api.get<PrintJob>(`/print/jobs/${id}`);
}

export function reprintJob(id: string, printerId?: string): Promise<PrintJobResult> {
  return api.post<PrintJobResult>(`/print/jobs/${id}/reprint`, printerId ? { printerId } : {});
}

export function cancelJob(id: string): Promise<PrintJobResult> {
  return api.post<PrintJobResult>(`/print/jobs/${id}/cancel`, {});
}

export function documentUrl(jobId: string): string {
  return `/api/print/jobs/${jobId}/document`;
}

// --- templates -------------------------------------------------------------

export function listTemplates(): Promise<{ items: TemplateInfo[] }> {
  return api.get<{ items: TemplateInfo[] }>('/print/templates');
}

export function getTemplateSample(kind: LabelKind): Promise<{ kind: LabelKind; payload: unknown }> {
  return api.get<{ kind: LabelKind; payload: unknown }>(`/print/templates/${kind}/sample`);
}

export function previewZpl(
  kind: LabelKind,
  payload: unknown,
  dpi?: number,
): Promise<{ zpl: string; dpi: number; kind: LabelKind }> {
  return api.post<{ zpl: string; dpi: number; kind: LabelKind }>(
    `/print/templates/${kind}/preview`,
    { payload, format: 'zpl', ...(dpi ? { dpi } : {}) },
  );
}

/**
 * The PNG preview arrives as binary, so it is fetched raw and turned into an
 * object URL. Callers must revoke it — `usePreview` in TemplatesPage does.
 */
export async function previewPng(
  kind: LabelKind,
  payload: unknown,
  options: { dpi?: number; scale?: number } = {},
): Promise<string> {
  const response = await apiRequest<Response>(`/print/templates/${kind}/preview`, {
    method: 'POST',
    body: {
      payload,
      format: 'png',
      scale: options.scale ?? 2,
      ...(options.dpi ? { dpi: options.dpi } : {}),
    },
    raw: true,
  });
  return URL.createObjectURL(await response.blob());
}

export function printLabel(input: {
  kind: LabelKind;
  payload: unknown;
  printerId: string;
  copies?: number;
}): Promise<PrintJobResult> {
  return api.post<PrintJobResult>('/print/label', input);
}
